/**
 * useRangeSelection.ts
 * Hook that exposes mouse-event handlers for Excel-style range selection
 * on the Skill Matrix grid.
 *
 * Usage:
 *   const { onCellMouseDown, onCellMouseEnter, onCellClick } = useRangeSelection();
 *   // Wire each handler onto the <TableCell> for (userKey, skillKey).
 *
 * Behaviour:
 *   - Plain click   → focus moves to the clicked cell (single-cell selection).
 *   - Shift+click   → extends selection from the current anchor to the clicked
 *                     cell (rectangle select).
 *   - Click+drag    → mousedown on A with button held → mouseenter B → mouseup
 *                     produces a selection from A to B.
 *
 * All store mutations go through useSkillMatrixStore.setFocus / extendSelection.
 */

import { useRef, useCallback, useEffect } from 'react';
import { useSkillMatrixStore } from '../../../../state/skillMatrixStore';
import type { CellCoord } from './selectionMath';

export interface RangeSelectionHandlers {
  onCellMouseDown: (coord: CellCoord, e: React.MouseEvent) => void;
  onCellMouseEnter: (coord: CellCoord, e: React.MouseEvent) => void;
  onCellClick: (coord: CellCoord, e: React.MouseEvent) => void;
}

export function useRangeSelection(): RangeSelectionHandlers {
  const store = useSkillMatrixStore;

  // Track whether a click+drag selection is currently in progress.
  const isDragging = useRef(false);
  // Track whether the primary mouse button is currently held down.
  const isMouseButtonDown = useRef(false);

  // Clean up the module-level button-state flag on mouseup anywhere in the window.
  useEffect(() => {
    const onMouseUp = () => {
      isMouseButtonDown.current = false;
    };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  const onCellMouseDown = useCallback(
    (coord: CellCoord, e: React.MouseEvent) => {
      // Only handle primary (left) button.
      if (e.button !== 0) return;

      isMouseButtonDown.current = true;
      isDragging.current = false;

      const { setFocus, extendSelection } = store.getState();

      // Shift+mousedown extends the existing selection from the anchor.
      if (e.shiftKey) {
        extendSelection(coord);
      } else {
        setFocus(coord);
      }
    },
    [store],
  );

  const onCellMouseEnter = useCallback(
    (coord: CellCoord, e: React.MouseEvent) => {
      // Only extend during a drag (primary button still held — buttons bitmask bit 0).
      if (!isMouseButtonDown.current || (e.buttons & 1) === 0) return;
      isDragging.current = true;
      const { extendSelection } = store.getState();
      extendSelection(coord);
    },
    [store],
  );

  const onCellClick = useCallback(
    (coord: CellCoord, e: React.MouseEvent) => {
      // If a drag just finished we already set selection via mouseenter events;
      // ignore the synthetic click that follows mouseup to avoid snapping back
      // to a single cell.
      if (isDragging.current) {
        isDragging.current = false;
        return;
      }

      const { setFocus, extendSelection } = store.getState();
      if (e.shiftKey) {
        extendSelection(coord);
      } else {
        setFocus(coord);
      }
    },
    [store],
  );

  return { onCellMouseDown, onCellMouseEnter, onCellClick };
}
